<template>
    <div class="sensor-container" :class="[$mq, sensorStatusClass]">
        <div class="sensor-content">
            <div class="first-line">
                <div class="sensor-name" :class="iconStatusClass">{{ sensor.name }}</div>
                <div class="status-icon" :class="iconStatusClass">
                    <i v-if="sensor.status === 1" class="i-andy-no-wifi i-andy-s"></i>
                    <i v-else-if="sensor.config == null || sensor.config.alert == false" class="i-andy-bell-disabled i-andy-s"></i>
                    <i v-else-if="hasOpenIncident && hasCurrentAlert" class="i-andy-exclamation i-andy-s"></i>
                    <i v-else-if="hasCurrentAlert" class="i-andy-menu-task i-andy-s"></i>
                    <i v-else class="i-andy-menu-checklist i-andy-s"></i>
                </div>
            </div>
            <div class="second-line">
                <div class="last-temperature" :class="temperatureStatusClass">
                    <span v-if="isHumidity && sensor.status == 2 && lastValue && lastValue.value != null" class="sensor-card-value-humidity">
                        <span class="sensor-card-value-humidity-icon">
                            <i class="i-andy-droplets i-andy-m"></i>
                        </span>
                        <span class="sensor-card-value-humidity-text">{{ formatReading(lastValue.value) }}&nbsp;{{ measurementUnit }}</span>
                    </span>
                    <template v-else>{{ sensor.status == 2 ? formatTemperatureValue(lastValue && lastValue.value) : '-' }}</template>
                </div>
                <div class="temperature-range" v-if="acceptableRange">
                    <i :class="rangeIconClass"></i>
                    <span>{{ acceptableRange }}</span>
                </div>
                <span v-else><i :class="rangeIconClass"></i>-</span>
            </div>

            <div class="status-badge" :class="[sensorStatusClass, { 'status-badge--empty': !hasStatusText }]">
                <div class="status" :class="[sensorStatusClass]">
                    {{ hasStatusText ? statusMessage : "\u00A0" }}
                </div>
                <div class="badge">
                    {{ sensorStatusClass === 'status-incident' ? $t('sensor.issue_time') : sensorStatusClass === 'status-alert' && hasAlert ? $t('sensor.alert_time') : ''}}
                </div>
            </div>

            <div class="sensor-footer">
                <div class="timestamp">
                    <div class="i-andy-history i-andy-s"></div>
                    <span v-if="lastValue">{{ formatDateShort(lastValue.date) }}</span>
                    <span v-else>{{ $t('sensor.no_data') }}</span>
                </div>

                <i class="i-andy-external i-andy-s"></i>
            </div>
        </div>
    </div>
</template>

<script>
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'
import { isHumiditySensor } from '@/utils/sensorStatus'
import { formatDecimal, formatTemperature, temperatureUnitSuffix } from '@/utils/numberFormat'

export default {
    name: 'sensor',
    props: {
        sensor: {
            type: Object,
            default: false
        }
    },
    data() {
        return {}
    },
    computed: {
        values() {
            return this.$store.getters['sensor/getTestoSensorsInfo'](this.sensor.id)
        },
        issueStates() {
            return this.$store.getters['getIssueStates']
        },
        lastValue() {
            return this.sensor.last_value
        },
        isHumidity() {
            return isHumiditySensor(this.sensor)
        },
        measurementUnit() {
            return this.isHumidity ? this.$t('sensor.grades_humidity') : '°C'
        },
        rangeIconClass() {
            return this.isHumidity ? 'i-andy-droplets i-andy-s' : 'i-andy-format-temperature i-andy-s'
        },
        hasOpenIncident() {
            if (!this.values || !Array.isArray(this.values.issuesList)) return false
            const readings = this.values.issuesList
            return readings.some((r) => r && r.issue && this.issueStates[r.issue.status].type !== 3)
        },
        hasCurrentAlert() {
            if (this.sensor.status === 1) return false
			const current = Number(this.sensor.last_value && this.sensor.last_value.value)
			const cond = this.sensor.config && this.sensor.config.condition
			const rawVal = this.sensor.config && this.sensor.config.value

			if (cond === '<') {
				const thr = Number(rawVal)
				return !isNaN(thr) ? current < thr : false
			}
			if (cond === '>') {
				const thr = Number(rawVal)
				return !isNaN(thr) ? current > thr : false
			}
			if (cond === '<>' || cond === '!<>') {
				if (typeof rawVal === 'string' && rawVal.includes(',')) {
					const parts = rawVal.split(',').map(v => parseFloat(String(v).trim()))
					const min = parts[0]
					const max = parts[1]
					if (isNaN(min) || isNaN(max)) return false
					const inside = current >= min && current <= max
					return cond === '<>' ? inside : !inside
				}
				return false
			}
            return false
        },
        hasAlert() {
            if (this.sensor.status === 1) return false
            if (this.hasOpenIncident) return false

            if (this.values && this.values.alertsList > 0) return true

            if (!this.sensor.values) return false
            return Object.values(this.sensor.values).some((v) => v && v.alert === true)
        },
        sensorStatusClass() {
            if (this.sensor.status === 1) return 'status-no-data'
            if (this.sensor.config == false || this.sensor.config.alert == false) return 'status-offline'
            if (this.hasOpenIncident) return 'status-incident'
            if (this.hasAlert || this.hasCurrentAlert) return 'status-alert'

            return 'status-ok'
        },
        temperatureStatusClass() {
            if (this.sensor.status === 1) return 'temp-no-data'

            if (this.hasOpenIncident && this.hasCurrentAlert) return 'temp-incident'

            if (this.hasOpenIncident) return 'temp-normal'

            return 'temp-normal'
        },
        iconStatusClass() {
            if (this.sensor.status === 1) return 'status-no-data'
            if (this.sensor.config == null || this.sensor.config.alert == false) return 'status-no-alert'
            if (this.hasOpenIncident && this.hasCurrentAlert) return 'status-incident'
            if (this.hasCurrentAlert) return 'status-alert'
            return 'status-ok'
        },
        statusMessage() {
            const hasIssues = this.values && Array.isArray(this.values.issuesList) && this.values.issuesList.length > 0
            if (hasIssues) {
                const count = this.values.issuesList.length
                const label = count === 1 ? this.$t('sensor.issue') : this.$t('home.issues.title')
                return `${count} ${label}`
            }
            if (this.sensor.status === 1) {
                return this.$t('sensor.no_data')
            }
            const alertsCount = this.values && typeof this.values.alertsList === 'number' ? this.values.alertsList : 0
            if (alertsCount > 0) {
                const label = alertsCount === 1 ? this.$t('sensor.alert.title') : this.$t('sensor.alert.multiple')
                return `${alertsCount} ${label}`
            }
            if (this.hasCurrentAlert && (this.sensor.config == null || this.sensor.config.alert == false)) {
                return this.$t('config.sensor.no_alert')
            }

            return this.$t('config.sensor.correct')
        },
        hasStatusText() {
            if (!this.sensor.config || this.sensor.config.alert !== true) return false
            if (this.sensor.status === 1) return false
            return this.sensorStatusClass !== 'status-ok' && (this.values && this.values.alertsList > 0)
        },
        rangeConnector() {
            return this.$tc('assets.timing.to', 1)
        },
        acceptableRange() {
            if (this.sensor.config == false) return null

            const config = this.sensor.config
            const condition = config.condition
            const value = config.value
            const unit = this.measurementUnit
            const formattedUnit = this.isHumidity ? `\u00A0${unit}` : temperatureUnitSuffix(unit, this.$i18n.locale)

            if (!condition || value == null || value === '') return null

            switch (condition) {
                case '!<>':
                    if (typeof value === 'string' && value.includes(',')) {
                        const [min, max] = value.split(',').map((v) => parseFloat(v.trim()))
                        return `${this.formatReading(min)}${formattedUnit}\u00A0${this.rangeConnector}\u00A0${this.formatReading(max)}${formattedUnit}`
                    }

                    return null

                case '<>':
                    if (typeof value === 'string' && value.includes(',')) {
                        const [min, max] = value.split(',').map((v) => parseFloat(v.trim()))
                        return `${this.formatReading(min)}${formattedUnit}\u00A0${this.rangeConnector}\u00A0${this.formatReading(max)}${formattedUnit}`
                    }

                    return null

                case '<':
                    return `>\u00A0${this.formatReading(value)}${formattedUnit}`

                case '>':
                    return `<\u00A0${this.formatReading(value)}${formattedUnit}`

                case '<=':
                    return `≤\u00A0${this.formatReading(value)}${formattedUnit}`

                case '>=':
                    return `≥\u00A0${this.formatReading(value)}${formattedUnit}`

                case '=':
                    return `=\u00A0${this.formatReading(value)}${formattedUnit}`

                case '!=':
                    return `≠\u00A0${this.formatReading(value)}${formattedUnit}`

                default:
                    return null
            }
        }
    },
    methods: {
        formatTemperatureValue(value) { return formatTemperature(value, this.$i18n.locale) },
        formatReading(value) {
            const formatted = formatDecimal(value, this.$i18n.locale)
            return formatted === '' ? '-' : formatted
        },
        formatDate(date) {
            date = date * 1000
            return formatDisplayDateFromStore(date, { includeTime: true })
        },
        formatDateShort(date) {
            return formatDisplayDateFromStore(date * 1000, { includeTime: true })
        },
        getTestoSensorsInfo(id) {
            return this.$store.getters['sensor/getTestoSensorsInfo'](id)
        }
    }
}
</script>

<style lang="scss" scoped>
.sensor-container {
    display: flex;
    flex-direction: column;

    cursor: pointer;
    background-color: $neutro-t90;
    @include border-radius(4px);
    padding: 16px;
    gap: 8px;
    min-width: 200px;
    width: calc(20% - 13px);
    border: 1px solid $neutro-dark;

    &:hover {
        border: 2px solid $main-light;
        padding: 15px;
        border-color: $main-light;
    }

    // Status color borders and backgrounds
    &.status-incident {
        background-color: #fff;

        .alert-badge {
            background-color: $error-dark;
        }

        .status-icon i {
            color: $error-dark;
        }
    }

    &.status-alert {
        background-color: #fff;

        .status-icon i {
            color: #ffc700;
        }
    }

    &.status-ok {
        background-color: #fff;

        .status-icon i {
            color: #348567;
        }
    }

    &.status-no-data {
        background-color: #fff;

        .status-icon i {
            color: $error-dark;
        }
    }

    // Alert badge for open incidents
    .alert-badge {
        position: absolute;
        top: 12px;
        right: 12px;
        background-color: $error-dark;
        color: #fff;
        width: 24px;
        height: 24px;
        @include border-radius(50%);
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        font-size: 12px;
        font-weight: bold;
        z-index: 10;
    }

    @keyframes pulse {
        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.8;
            transform: scale(1.05);
        }
    }

    .sensor-content {
        @include display-flex();
        @include flex-direction(column);
        gap: 8px;

        .first-line,
        .second-line,
        .sensor-footer {
            @include display-flex();
            justify-content: space-between;
            align-self: stretch;
        }

        .first-line {
            @include align-items(center);
            margin-bottom: 4px;
        }

        .sensor-name {
            @include text-ellipsis();
            color: #2d2e2f;
            font-family: $text-sbold;
            font-weight: 700;
            font-size: 16px;
            line-height: normal;
            flex: 1;
            &.status-no-data {
                color: $neutro-s20;
            }
        }

        .status-icon {
            width: 28px;
            height: 28px;
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            flex-shrink: 0;
            margin-left: 8px;
            border-radius: 4px;

            i {
                font-size: 24px;
            }

            // Icon colors based on priority: no-data > incident > alert > ok
            &.status-incident { 
                background-color: $error-t90;
                i {
                    color: $error-dark; // Red - open incident
                }
            }

            &.status-alert { 
                background-color: $accent-t90;
                i {
                    color: #cc9f00; // Yellow - current value out of range
                }
            }

            &.status-ok {
                background-color: $done-t90;
                i {
                    color: #348567; // Green - current value within range
                }
            }

            &.status-no-data {
                background-color: $error-t90;
                i {
                    color: $error-dark; // Red - no connection
                }
            }

            &.status-no-alert {
                background-color: $neutro-t80;
                i {
                    color: $color-neutral-500;
                }
            }
        }

        .second-line {
            @include align-items(center);
            gap: 8px;
            min-width: 0; // Allow flex items to shrink below their minimum content size
        }

        .last-temperature {
            font-weight: 700;
            line-height: normal;
            font-size: 24px;
            text-align: left;
            font-family: Avenir-Bold;
            white-space: nowrap; // Prevent text wrapping
            flex-shrink: 0;

            &.temp-incident {
                color: $error-dark;
            }

            &.temp-alert {
                color: $color-warning-500;
            }

            &.temp-neutral {
                color: $neutro-s80; // Neutral gray when incident/alert but reading is correct
            }

            &.temp-normal {
                color: #2d2e2f;
            }

            &.temp-no-data {
                color: $neutro-s20;
            }

            .no-data-temp {
                color: $inactive-s60;
            }

            .sensor-card-value-humidity {
                display: flex;
                align-items: center;
                gap: 4px;
                font-family: Avenir-Bold;
                font-size: 24px;
                font-weight: 700;
                line-height: normal;

                .sensor-card-value-humidity-icon {
                    display: flex;
                    width: 24px;
                    height: 24px;
                    justify-content: center;
                    align-items: center;
                    aspect-ratio: 1 / 1;
                    flex-shrink: 0;

                    .i-andy-droplets {
                        color: $info-dark;
                    }
                }

                .sensor-card-value-humidity-text {
                    font-family: Avenir-Bold;
                    font-size: 24px;
                    font-weight: 700;
                    line-height: normal;
                    color: inherit;
                }

            }
        }

        .temperature-range {
            @include display-flex();
            @include align-items(center);
            font-size: 11px;
            color: $inactive-s60;
            font-family: $text;
            font-weight: 400;
            text-align: right;
            line-height: normal;
            gap: 4px;
            white-space: nowrap; // Prevent wrapping
            flex-shrink: 1; // Can shrink if needed, but won't break lines
            min-width: 0;

            i {
                color: $inactive-s60;
                flex-shrink: 0;
                font-size: 12px;

                &.i-andy-droplets {
                    color: $neutro-s40;
                }
            }

            span {
                white-space: nowrap;
                overflow: visible;
            }
        }

        .status-badge {
            @include display-flex();
            gap: 4px;
            padding: 0;
            margin-top: 8px;
            margin-bottom: 8px;
            min-height: 22px;
            @include border-radius(4px);

            div {
                font-size: 16px;

            }
            .status {
                font-family: $text-sbold;
                font-weight: 700;
            }

            .status-incident {
                color: $error-dark;
            }

            .status-alert {
                color: #cc9f00;
            }

            .status-ok {
                color: #348567;
            }

            .status-no-data {
                color: $neutro-s80;
            }

            .status-badge--empty {
                background-color: transparent;
                color: transparent;
            }
        }

        .sensor-footer {
            @include display-flex();
            @include justify-content(space-between);
            @include align-items(center);
            margin-top: 4px;

            .timestamp {
                @include display-flex();
                @include align-items(center);
                gap: 4px;
                font-size: 14px;
                color: #373d3f;
                font-family: $text;
            }

            .i-andy-external {
                color: $main-light;
            }
        }
    }

    // Landscape mode (tablets horizontal): 4 cards per row
    &.landscape {
        width: calc(25% - 12px);
        min-width: 200px;

        &:hover {
            padding: 15px;
        }

        .sensor-content {
            .sensor-name {
                @include font-size(m);
            }

            .temperature-range {
                @include font-size(sm);

                i {
                    font-size: 16px;
                }
            }

            .status-badge {
                @include font-size(sm);
                padding: 6px 10px;
            }

            .sensor-footer {
                .timestamp {
                    @include font-size(sm);
                }
            }
        }
    }

    // Portrait mode (tablets vertical, mobile): 3 cards per row
    &.portrait {
        width: calc(33.333% - 11px);
        min-width: 180px;
        padding: 16px;

        &:hover {
            padding: 15px;
        }

        .sensor-content {
            gap: 8px;

            .sensor-name {
                @include font-size(m);
            }

            .temperature-range {
                @include font-size(xs);

                i {
                    font-size: 14px;
                }
            }

            .status-badge {
                @include font-size(xs);
                padding: 4px 8px;
            }

            .sensor-footer {
                .timestamp {
                    @include font-size(xs);
                }
            }
        }
    }
}

// Additional responsive breakpoints for better card sizing
// Large Desktop (>1400px): 5 cards per row
@media (min-width: 1401px) {
    .sensor-container:not(.landscape):not(.portrait) {
        width: calc(20% - 13px) !important;
        min-width: 220px;
    }
}

// Desktop (1200px - 1400px): 4 cards per row
@media (min-width: 1200px) and (max-width: 1400px) {
    .sensor-container:not(.landscape):not(.portrait) {
        width: calc(25% - 12px) !important;
        min-width: 200px;
    }
}

// Medium Desktop/Tablet (950px - 1199px): 3 cards per row
@media (min-width: 950px) and (max-width: 1199px) {
    .sensor-container:not(.landscape):not(.portrait) {
        width: calc(33.333% - 11px) !important;
        min-width: 260px; // Increased to 260px to ensure enough space for temperature + range
    }
}

// Tablet (700px - 949px): 3 cards per row (smaller)
@media (min-width: 700px) and (max-width: 949px) {
    .sensor-container {
        width: calc(33.333% - 11px) !important;
        min-width: 180px;
    }
}

// Mobile Large (500px - 699px): 2 cards per row
@media (min-width: 500px) and (max-width: 699px) {
    .sensor-container {
        width: calc(50% - 8px) !important;
        min-width: 160px;
    }
}

// Mobile Small (<500px): 1 card per row
@media (max-width: 499px) {
    .sensor-container {
        width: 100% !important;
        min-width: 280px;
    }
}
</style>
